'use client'

import { useAuth } from '@/contexts/AuthContext'
import { useSidebar } from '@/contexts/SidebarContext'
import { useUserCompanies } from '@/features/companies'
import {
  type SimplifiedNotification,
  useNotificationCountsRealtime,
  useNotificationsRealtime,
} from '@/features/notifications'
import { useCompanyAccess } from '@/hooks/useCompanyAccess'
import { useInteractionNotifications } from '@/hooks/useInteractionNotifications'
import { auth } from '@/lib/firebase'
import { cn } from '@/lib/utils'
import { UserType } from '@/types'
import {
  canViewMediaMembers,
  canViewPRMembers,
  canViewPosts,
  canViewPrivacyPolicy,
  canViewSupport,
  getCompanyHref,
} from '@/utils/userPermissions'
import {
  Bell,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  type FileText,
  HelpCircle,
  House,
  LogOut,
  Megaphone,
  Menu,
  Newspaper,
  Shield,
  Users,
  X,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { type ComponentProps, type PropsWithChildren, useEffect, useState } from 'react'
import { EmailVerificationBanner } from './EmailVerificationBanner'
import { Logo } from './Logo'
import { LogoAlt, LogoImage } from './LogoAlt'
import { DisabledCompanyMessage } from './companies/DisabledCompanyMessage'

export interface LayoutProps {
  title?: string
  withGoBack?: boolean
}

interface SidebarItemConfig {
  key: string
  href: string | (() => string)
  icon: typeof FileText
  label: string
  isActive: (path: string) => boolean
  canView: () => boolean
}

export const Layout = ({ children, title, withGoBack }: PropsWithChildren<LayoutProps>) => {
  const path = usePathname()
  const router = useRouter()
  const { userData, company, setCompany, setCurrentUser, currentUser, isLoading, isAuthReady } = useAuth()
  const { canAccessSidebar, allowedSidebarItems, disabledMessage, isCompanyDisabled } = useCompanyAccess()

  // Helper functions to convert null to undefined for better type safety
  const safeUserData = userData ?? undefined
  const safeCompany = company ?? undefined

  // All hooks must be called before any conditional returns
  const { data: userCompaniesData } = useUserCompanies(userData?.id || '', {
    enabled: !!userData?.id,
  })

  const { data: counts = { total: 0, unread: 0, followRequests: 0 } } = useNotificationCountsRealtime(
    userData?.id || '',
    company?.id || '',
  )

  // Removed duplicate useNotificationsRealtime - only used in NotificationsPage
  // const { data: notifications = [] } = useNotificationsRealtime(userData?.id || '', 'web')

  // Initialize interaction notifications system
  useInteractionNotifications({
    enabled: !!userData?.id,
  })

  // All state hooks must be called before any conditional returns
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileCompaniesMenuOpen, setMobileCompaniesMenuOpen] = useState(false)

  // Use sidebar context instead of local state
  const { collapsed: sidebarCollapsed, toggleCollapsed: toggleSidebar } = useSidebar()

  // Redirect to signin if not authenticated (only after auth is ready)
  useEffect(() => {
    if (isAuthReady && !isLoading && !currentUser && !userData) {
      router.push('/signin')
    }
  }, [isAuthReady, isLoading, currentUser, userData, router])

  useEffect(() => {
    if (userCompaniesData?.companies?.length && userCompaniesData.companies.length > 0 && !company) {
      setCompany(userCompaniesData.companies[0])
    }
  }, [userCompaniesData, company, setCompany])

  // Don't render layout until auth is ready or user is authenticated
  if (!isAuthReady || (isLoading && !currentUser)) {
    return (
      <div className='flex h-screen w-full items-center justify-center bg-neutral-50'>
        <div className='text-center'>
          <div className='mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-neutral-300 border-r-neutral-600' />
          <p className='text-sm text-neutral-600'>Loading...</p>
        </div>
      </div>
    )
  }

  // If auth is ready but user is not authenticated, show loading while redirecting
  if (isAuthReady && !currentUser && !userData) {
    return (
      <div className='flex h-screen w-full items-center justify-center bg-neutral-50'>
        <div className='text-center'>
          <div className='mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-neutral-300 border-r-neutral-600' />
          <p className='text-sm text-neutral-600'>Redirecting...</p>
        </div>
      </div>
    )
  }

  // Use total count from cloud functions (maintained server-side for data integrity)
  const totalRequestsCount = counts.total

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev)

  // Define sidebar items configuration
  const sidebarItems: SidebarItemConfig[] = [
    {
      key: 'dashboard',
      href: '/',
      icon: House,
      label: 'Dashboard',
      isActive: (path) => path === '/',
      canView: () => allowedSidebarItems.includes('dashboard'),
    },
    {
      key: 'posts',
      href: '/posts',
      icon: Bookmark,
      label: 'Posts',
      isActive: (path) => !!path?.startsWith('/posts'),
      canView: () => allowedSidebarItems.includes('posts') && canViewPosts(safeUserData, safeCompany),
    },
    {
      key: 'companies',
      href: () => getCompanyHref(safeUserData, safeCompany),
      icon: Megaphone,
      label: userData?.userType === UserType.SUPER_ADMIN || userData?.isRoot ? 'Companies' : 'Company',
      isActive: (path) => !!path?.startsWith('/companies'),
      canView: () => allowedSidebarItems.includes('companies'),
    },
    {
      key: 'users',
      href: () => {
        const isSuperAdmin = userData?.userType === UserType.SUPER_ADMIN || userData?.isRoot
        return isSuperAdmin ? '/users' : '/pr-members'
      },
      icon: Users,
      label: 'Users',
      isActive: (path) => !!path?.startsWith('/pr-members') || !!path?.startsWith('/users'),
      canView: () => allowedSidebarItems.includes('pr-members') && canViewPRMembers(safeUserData, safeCompany),
    },
    {
      key: 'media-members',
      href: '/media-members',
      icon: Newspaper,
      label: 'Media & Fans',
      isActive: (path) => !!path?.startsWith('/media-members'),
      canView: () => allowedSidebarItems.includes('media-members') && canViewMediaMembers(safeUserData, safeCompany),
    },
    {
      key: 'privacy-policy',
      href: '/privacy-policy',
      icon: Shield,
      label: 'Privacy Policy',
      isActive: (path) => !!path?.startsWith('/privacy-policy'),
      canView: () => allowedSidebarItems.includes('privacy-policy') && canViewPrivacyPolicy(safeUserData),
    },
    {
      key: 'support',
      href: '/support',
      icon: HelpCircle,
      label: 'Help & Support',
      isActive: (path) => !!path?.startsWith('/support'),
      canView: () => allowedSidebarItems.includes('support') && canViewSupport(safeUserData),
    },
  ]

  const handleLogout = async () => {
    try {
      await auth.signOut()
      setCurrentUser(null)
      router.push('/signin')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  // If company is disabled and user cannot access sidebar, show disabled message
  if (isCompanyDisabled && !canAccessSidebar) {
    return <DisabledCompanyMessage message={disabledMessage} showHomeButton={false} />
  }

  const collapsed = sidebarCollapsed && !mobileMenuOpen

  return (
    <div className='flex h-screen w-full max-w-full overflow-x-hidden flex-col'>
      <div className='flex flex-1'>
        {/* Show disabled company banner if company is disabled but user can access sidebar */}
        {isCompanyDisabled && canAccessSidebar && disabledMessage && (
          <div className='absolute top-0 left-0 right-0 z-50 bg-red-600 text-white text-center py-2 px-4 text-sm'>
            {disabledMessage}
          </div>
        )}

        <div
          className={cn(
            'flex-shrink-0 flex flex-col bg-white',
            collapsed ? 'border-r-0' : 'border-r border-neutral-300',
            'transition-all duration-300 ease-in-out',
            // Desktop: always visible, mobile: hidden unless menu is open
            mobileMenuOpen
              ? 'flex absolute w-full h-screen z-50'
              : cn('hidden sm:flex sm:fixed sm:left-0 sm:top-0 sm:h-screen', collapsed ? 'sm:w-20' : 'w-56'),
            // Add top margin if company is disabled
            isCompanyDisabled && canAccessSidebar && 'mt-10',
          )}
        >
          {mobileMenuOpen && (
            <div className='p-3 px-4 gap-2 flex justify-between items-center border-b border-neutral-200'>
              {!title ? (
                <Link href='/dashboard' className='cursor-pointer hover:opacity-80 transition-opacity duration-200'>
                  <Logo width={71} height={32} />
                </Link>
              ) : (
                <h1 className='font-semibold text-base leading-6 flex-1 whitespace-nowrap overflow-hidden text-ellipsis'>
                  {title}
                </h1>
              )}
              <button
                type='button'
                onClick={toggleMobileMenu}
                className={'p-2.5 rounded-md bg-white self-end hover:bg-neutral-100 cursor-pointer'}
              >
                <X className='h-5 w-5' />
              </button>
            </div>
          )}

          <div className='flex-1 p-4 gap-4 flex flex-col'>
            {!mobileMenuOpen && (
              <div className='flex justify-center items-center'>
                {path === '/' ? (
                  <div className='cursor-default'>
                    {sidebarCollapsed ? (
                      <p key='sidebar-text-logo' className='font-semibold text-sm leading-5'>
                        RTSC
                      </p>
                    ) : (
                      <LogoAlt key='sidebar-image-logo' width={120} height={32} sizes='120px' />
                    )}
                  </div>
                ) : (
                  <Link href='/dashboard' className='cursor-pointer hover:opacity-80 transition-opacity duration-200'>
                    {sidebarCollapsed ? (
                      <p key='sidebar-text-logo' className='font-semibold text-sm leading-5'>
                        RTSC
                      </p>
                    ) : (
                      <LogoImage key='sidebar-image-logo' width={120} height={32} sizes='120px' />
                    )}
                  </Link>
                )}
              </div>
            )}

            <ul className='flex flex-col gap-1 h-full'>
              {sidebarItems
                .filter((item) => item.canView())
                .map((item) => (
                  <ListItem
                    key={item.key}
                    active={item.isActive(path)}
                    href={typeof item.href === 'function' ? item.href() : item.href}
                    icon={item.icon}
                    collapsed={collapsed}
                    data-tour={`sidebar-${item.key}`}
                    id={`sidebar-${item.key}`}
                    className={item.key === 'dashboard' ? 'dashboard-tour-wrapper' : undefined}
                  >
                    {item.label}
                  </ListItem>
                ))}

              <ListItem
                onClick={handleLogout}
                icon={LogOut}
                collapsed={collapsed}
                href='#'
                className='mt-auto'
                active={false}
              >
                Logout
              </ListItem>
            </ul>
          </div>

          <Link href='/profile' className='hover:bg-neutral-100 group cursor-pointer'>
            <div className='border-t border-neutral-200 p-4 flex gap-3 items-center'>
              <Image
                alt='User avatar'
                width={36}
                height={36}
                className='rounded-full h-9 w-9 object-cover'
                src={userData?.profilePicUrl || '/images/avatar.jpeg'}
                blurDataURL='data:image/jpg;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAGCAMAAADNLv/0AAAAAXNSR0IB2cksfwAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAAK5QTFRFwsHBoZKSw7e29+np9+zs+Pb27evr/fz87+7u+/z8l4iIjQ4JoQkM14WCymZjpoWEk42Ns7Kyc3Fxvby8momJnAsLnwkM2dnZzZWVwry8kI6PlpSV19bX5+bmk4mIWRANbSso3dzcz7a2uLe4lJOTwcDBi4mKzMvM19bWzcLC7OXk/fDw8vLy7+/v9PX18PDw8fLy/f39//39/enn/ezq/eHf5+Xl4eHh5eXl39/fu+ppxwAAABxJREFUeJxjZGCEAy4EUwTBlEMwNRBMIyj9/yIAGUQCoeTy7I8AAAAASUVORK5CYII='
              />
              {(!sidebarCollapsed || mobileMenuOpen) && (
                <div className='flex flex-col'>
                  <p className='text-sm font-medium text-neutral-700 leading-5 whitespace-nowrap overflow-hidden text-ellipsis'>
                    {userData?.fullName || '--'}
                  </p>
                  <p className='text-xs font-medium leading-4 text-neutral-500 group-hover:underline'>View profile</p>
                </div>
              )}
            </div>
          </Link>

          {!mobileMenuOpen && (
            <button
              type='button'
              onClick={toggleSidebar}
              className={cn(
                'p-0.5 rounded-full border border-neutral-200 bg-white hover:bg-neutral-100 cursor-pointer',
                !mobileMenuOpen && 'absolute bottom-5 -right-3',
              )}
            >
              {sidebarCollapsed ? <ChevronRight className='h-5 w-5' /> : <ChevronLeft className='h-5 w-5' />}
            </button>
          )}
        </div>

        <div
          className={cn(
            'flex flex-col flex-grow overflow-x-hidden',
            isCompanyDisabled && canAccessSidebar && 'mt-10',
            !mobileMenuOpen && !collapsed && 'sm:ml-56',
            !mobileMenuOpen && collapsed && 'sm:ml-20',
          )}
        >
          {/* Email verification banner */}
          <EmailVerificationBanner />

          <div
            className={cn(
              'bg-white flex items-center border-b border-neutral-200 py-3 pl-0 pr-4 gap-2',
              mobileCompaniesMenuOpen && 'justify-between',
            )}
          >
            {!!mobileCompaniesMenuOpen && (
              <button
                type='button'
                onClick={() => setMobileCompaniesMenuOpen(false)}
                className='p-2.5 rounded hover:bg-neutral-100 cursor-pointer'
              >
                <X className='h-5 w-5' />
              </button>
            )}
            {!mobileCompaniesMenuOpen && withGoBack && (
              <button
                type='button'
                onClick={router.back}
                className='p-2.5 ml-4 rounded hover:bg-neutral-100 cursor-pointer'
              >
                <ChevronLeft className='h-5 w-5' />
              </button>
            )}
            {!mobileCompaniesMenuOpen && !withGoBack && (
              <button
                type='button'
                onClick={toggleMobileMenu}
                className='p-2.5 ml-4 rounded hover:bg-neutral-100 cursor-pointer sm:hidden'
              >
                <Menu className='h-5 w-5' />
              </button>
            )}
            {!mobileCompaniesMenuOpen && (
              <h1
                className={cn(
                  'font-semibold text-base leading-6 text-center sm:flex-1 sm:text-left sm:block whitespace-nowrap overflow-hidden text-ellipsis',
                  !!title && 'max-sm:flex-1',
                  !withGoBack && 'sm:pl-4',
                )}
              >
                {title}
              </h1>
            )}
            {!mobileCompaniesMenuOpen && !title && <Logo width={71} height={32} className='sm:hidden m-auto' />}
            <div className='flex items-center gap-2 xs:gap-6'>
              {/* Show Super Admin badge for super admins, otherwise show company info */}
              {userData?.userType === UserType.SUPER_ADMIN || userData?.isRoot ? (
                <div className='border border-red-300 px-3 py-1 rounded-md bg-white'>
                  <p className='text-xs font-medium text-red-600 uppercase tracking-wide'>SUPERADMIN</p>
                </div>
              ) : (
                company && (
                  <div className='flex items-center gap-2 px-3 py-1.5 rounded-md'>
                    <Image
                      placeholder='blur'
                      alt='Company avatar'
                      width={28}
                      height={28}
                      src={company?.logoUrl || '/images/avatar.jpeg'}
                      className='rounded-full h-7 w-7 object-cover'
                      blurDataURL='data:image/jpg;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAGCAMAAADNLv/0AAAAAXNSR0IB2cksfwAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAAK5QTFRFwsHBoZKSw7e29+np9+zs+Pb27evr/fz87+7u+/z8l4iIjQ4JoQkM14WCymZjpoWEk42Ns7Kyc3Fxvby8momJnAsLnwkM2dnZzZWVwry8kI6PlpSV19bX5+bmk4mIWRANbSso3dzcz7a2uLe4lJOTwcDBi4mKzMvM19bWzcLC7OXk/fDw8vLy7+/v9PX18PDw8fLy/f39//39/enn/ezq/eHf5+Xl4eHh5eXl39/fu+ppxwAAABxJREFUeJxjZGCEAy4EUwTBlEMwNRBMIyj9/yIAGUQCoeTy7I8AAAAASUVORK5CYII='
                    />
                    <p className='font-semibold text-sm leading-5 text-neutral-900 max-w-36 xs:max-w-48 truncate'>
                      {company.name || 'No Company'}
                    </p>
                  </div>
                )
              )}
              <Link href='/notifications'>
                <button
                  type='button'
                  id='notification-bell'
                  className={cn(
                    'relative p-2.5 rounded hover:bg-neutral-100 cursor-pointer hidden sm:block',
                    mobileCompaniesMenuOpen && 'block',
                  )}
                >
                  <Bell className='h-5 w-5' />
                  {totalRequestsCount > 0 && (
                    <span className='absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium'>
                      {totalRequestsCount > 99 ? '99+' : totalRequestsCount}
                    </span>
                  )}
                </button>
              </Link>
            </div>
          </div>
          <div className='flex-col flex-grow overflow-y-auto overflow-x-hidden bg-neutral-100'>{children}</div>
        </div>
      </div>
    </div>
  )
}

const ListItem = ({
  children,
  collapsed,
  active,
  icon: Icon,
  ...props
}: PropsWithChildren<
  ComponentProps<typeof Link> & { collapsed: boolean; active: boolean; icon: typeof FileText; 'data-tour'?: string }
>) => {
  const { 'data-tour': dataTour, id, ...linkProps } = props
  return (
    <li>
      <Link
        {...linkProps}
        data-tour={dataTour}
        id={id}
        className={cn(
          'flex items-center gap-3 p-2 rounded-md text-neutral-500 font-medium text-sm leading-5 hover:bg-neutral-100',
          active && 'font-semibold text-neutral-800 bg-primary-100 hover:bg-primary-100',
          active && collapsed && 'text-primary-800',
          linkProps.className,
        )}
      >
        <Icon className='h-6 w-6' />
        {!collapsed && (
          <span className='flex-1 shrink whitespace-nowrap overflow-hidden text-ellipsis'>{children}</span>
        )}
        {!collapsed && active && <div className='w-1 bg-primary-500 self-stretch rounded-xs' />}
      </Link>
    </li>
  )
}
